import { useMutation, ApolloError } from '@apollo/client'
import { ZoneFragment } from '@libs/apollo/fragments'

import { MUTATION_CREATE_ZONE } from '@libs/apollo/mutations'
import { ZONING } from '@libs/apollo/queries'

import { Zone, ZoneCreateInput, MutationCreateZoneArgs, LocalFile, ProjectFileType } from '@types'
import { useSetProjectFileToZone } from '@hooks/project/set-project-file-to-zone.hook'
import { useCreateProjectFile } from '@hooks/project/create-project-file.hook'

interface Data {
  createZone?: Zone
}

export const useCreateZone = (): {
  createZone: (input: ZoneCreateInput, plans: LocalFile[]) => Promise<void>
  loading: boolean
  error: ApolloError | undefined
  data?: Data | null
} => {
  const { createProjectFile } = useCreateProjectFile()
  const { setProjectFileToZone } = useSetProjectFileToZone()

  const [createZone, { loading, error, data }] = useMutation<Data, MutationCreateZoneArgs>(MUTATION_CREATE_ZONE, {
    notifyOnNetworkStatusChange: true,
  })

  const _createZone = async (input: ZoneCreateInput, plans: LocalFile[]): Promise<void> => {
    try {
      // CREATE ZONE
      const result = await createZone({
        variables: { input },
        refetchQueries: [
          {
            query: ZONING,
            variables: { input: { projectId: input.projectId } },
          },
        ],
        awaitRefetchQueries: true,
        update(cache, { data }) {
          const createZone = data?.createZone

          if (createZone && createZone.parentId) {
            const zone: Zone | null = cache.readFragment({
              id: `Zone:${input.parentId}`,
              fragmentName: 'ZoneFragment',
              fragment: ZoneFragment,
            })

            if (zone) {
              cache.writeFragment({
                id: `Zone:${input.parentId}`,
                fragmentName: 'ZoneFragment',
                fragment: ZoneFragment,
                data: {
                  ...zone,
                  children: [...zone.children, createZone],
                },
              })
            }
          }
        },
      })

      // PROJECT FILE
      if (result.data?.createZone) {
        const zoneId = result.data?.createZone.id

        await Promise.all(
          plans.map(async (file) => {
            // CREATION PROJECT FILE
            const projectFile = await createProjectFile(
              {
                projectId: input.projectId,
                type: ProjectFileType.TypePlan,
                name: file.name,
              },
              file,
            )

            if (projectFile) {
              // LINK PROJECT FILE
              await setProjectFileToZone({ projectFileId: projectFile.id, zoneId })
            }
          }),
        )
      }
    } catch (error) {
      console.error(error)
    }
  }

  return { createZone: _createZone, loading, error, data }
}
